import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { create, getByAdmission } from "../../services/PatientOperationService";
import { getAllDoctors } from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { getAll } from "../../services/OperationTypeService";

const operationStatusList = ["Scheduled", "Completed", "Cancelled", "Postponed"];

const validationSchema = Yup.object({
  operationName: Yup.object().nullable().required("Operation Name is required"),
  operationDate: Yup.mixed()
    .nullable()
    .transform((value) => (dayjs.isDayjs(value) ? value.toDate() : value))
    .required("Operation Date is required")
    .typeError("Invalid date"),
  operationTime: Yup.mixed()
    .nullable()
    .transform((value) => (dayjs.isDayjs(value) ? value.toDate() : value))
    .required("Operation Time is required")
    .typeError("Invalid time"),
  surgeonId: Yup.string().required("Surgeon is required"),
  nurseIds: Yup.array().min(1, "Select at least one nurse").required("Nurse selection is required"),
  operationStatus: Yup.string().required("Operation Status is required"),
  anethetistId: Yup.string().required("Anestheologist is required"),
});

const PatientOperation = ({ open, onClose, admissionId, patientId, patient_info = {} }) => {
  const [doctorList, setDoctorList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [admissionOperations, setAdmissionOperations] = useState([]);
  const [duplicateError, setDuplicateError] = useState("");

  useEffect(() => {
    if (open) {
      fetchAllDoctors();
      fetchAllStaff();
      fetchOperationTypes();
      fetchOperationsForAdmission();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      operationName: null,
      operationDate: null,
      operationTime: null,
      surgeonId: "",
      anethetistId: "",
      nurseIds: [],
      preOpNotes: "",
      postOpNotes: "",
      operationStatus: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const operationDate = values.operationDate?.format("YYYY-MM-DD");
        const operationTime = values.operationTime?.format("HH:mm");

        const isDuplicate = admissionOperations.some((op) => {
          const dbDate = dayjs(new Date(op.operationDate[0], op.operationDate[1] - 1, op.operationDate[2])).format("YYYY-MM-DD");
          const dbTime = dayjs().hour(op.operationTime[0]).minute(op.operationTime[1]).format("HH:mm");

          return (
            dbDate === operationDate &&
            dbTime === operationTime &&
            op.admissionId === admissionId
          );
        });

        if (isDuplicate) {
          alert("❌ Duplicate Operation:\nAn operation with the same date, and time already exists for this admission.");
          return;
        }

        const payload = {
          patientAssignmentId: patientId,
          admissionId,
          operationName: values.operationName?.label || values.operationName?.name || "",
          operationDate,
          operationTime,
          surgeonId: parseInt(values.surgeonId),
          anesthetistId: parseInt(values.anethetistId),
          nurseIds: Array.isArray(values.nurseIds) ? values.nurseIds.join(",") : "",
          preOpNotes: values.preOpNotes,
          postOpNotes: values.postOpNotes,
          operationStatus: values.operationStatus,
        };

        await create(payload);
        alert("✅ Operation submitted successfully");
        resetForm();
        onClose();
      } catch (error) {
        console.error("❌ Operation submission failed:", error);
        alert("⚠️ Failed to submit operation");
      }
    }
  });

  const fetchAllDoctors = async () => {
    try {
      const allDoctors = await getAllDoctors();
      setDoctorList(allDoctors);
    } catch (error) {
      console.error("Failed to fetch doctors", error);
      setDoctorList([]);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const allStaff = await getAllStaff();
      setStaffList(allStaff);
    } catch (error) {
      console.error("Failed to fetch staff", error);
      setStaffList([]);
    }
  };

  const fetchOperationTypes = async () => {
    try {
      const allOperation = await getAll();
      setOperationTypes(allOperation);
    } catch (error) {
      console.error("Failed to fetch operations", error);
      setOperationTypes([]);
    }
  };

  const fetchOperationsForAdmission = async () => {
    try {
      const operations = await getByAdmission(admissionId);
      setAdmissionOperations(operations || []);
    } catch (error) {
      console.error("Failed to fetch operations for admission", error);
      setAdmissionOperations([]);
    }
  };

  return (
    <BasicModal open={open} onClose={() => { onClose(); formik.resetForm(); }} sx={COMPACT_FORM_MODAL_SX}>
      <Box px={3} pb={3}>
        <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
          Patient Operation & Surgeries
        </Typography>

        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={operationTypes}
                getOptionLabel={(option) => option.name || ""}
                value={formik.values.operationName}
                onChange={(_, value) => formik.setFieldValue("operationName", value)}
                onBlur={formik.handleBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="operationName"
                    label="Operation Name"
                    fullWidth
                    error={formik.touched.operationName && Boolean(formik.errors.operationName)}
                    helperText={
                      formik.touched.operationName &&
                      formik.errors.operationName?.name
                        ? formik.errors.operationName.name
                        : formik.errors.operationName
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Operation Date"
                  value={formik.values.operationDate}
                  onChange={(value) => formik.setFieldValue("operationDate", value)}
                  onBlur={formik.handleBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="operationDate"
                      fullWidth
                      sx={{ height: "100%" }}
                      error={formik.touched.operationDate && Boolean(formik.errors.operationDate)}
                      helperText={formik.touched.operationDate && formik.errors.operationDate}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="Operation Time"
                  value={formik.values.operationTime}
                  onChange={(value) => formik.setFieldValue("operationTime", value)}
                  onBlur={formik.handleBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="operationTime"
                      fullWidth
                      sx={{ height: "100%" }}
                      error={
                        formik.touched.operationTime &&
                        (Boolean(formik.errors.operationTime) || Boolean(duplicateError))
                      }
                      helperText={
                        (formik.touched.operationTime && formik.errors.operationTime) ||
                        duplicateError
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="surgeonId"
                label="Surgeon"
                value={formik.values.surgeonId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.surgeonId && Boolean(formik.errors.surgeonId)}
                helperText={formik.touched.surgeonId && formik.errors.surgeonId}
              >
                {doctorList
                  .filter((doctor) => doctor.userRole === "surgeon")
                  .map((surgeon) => (
                    <MenuItem key={surgeon.id} value={String(surgeon.id)}>
                      {surgeon.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="anethetistId"
                label="Anethetist"
                value={formik.values.anethetistId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.anethetistId && Boolean(formik.errors.anethetistId)}
                helperText={formik.touched.anethetistId && formik.errors.anethetistId}
              >
                {staffList
                  .filter((an) => an.userRole === "anesthesiologist")
                  .map((an) => (
                    <MenuItem key={an.id} value={an.id}>
                      {an.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="nurseIds"
                label="Nurses"
                value={formik.values.nurseIds}
                onChange={(e) => formik.setFieldValue("nurseIds", e.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.nurseIds && Boolean(formik.errors.nurseIds)}
                helperText={formik.touched.nurseIds && formik.errors.nurseIds}
                SelectProps={{ multiple: true }}
              >
                {staffList
                  .filter((staff) => staff.userRole === "staff")
                  .map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                name="operationStatus"
                label="Operation Status"
                value={formik.values.operationStatus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.operationStatus && Boolean(formik.errors.operationStatus)}
                helperText={formik.touched.operationStatus && formik.errors.operationStatus}
              >
                {operationStatusList.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                multiline
                minRows={3}
                fullWidth
                id="preOpNotes"
                name="preOpNotes"
                label="Pre Op Notes"
                value={formik.values.preOpNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.preOpNotes && Boolean(formik.errors.preOpNotes)}
                helperText={formik.touched.preOpNotes && formik.errors.preOpNotes}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                multiline
                minRows={3}
                fullWidth
                id="postOpNotes"
                name="postOpNotes"
                label="Post Op Notes"
                value={formik.values.postOpNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.postOpNotes && Boolean(formik.errors.postOpNotes)}
                helperText={formik.touched.postOpNotes && formik.errors.postOpNotes}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={formik.isSubmitting}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default PatientOperation;
